import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useCondominios } from "@/hooks/condominio/useCondominioData";
import { useCondominioImpersonation } from "@/contexts/CondominioImpersonationContext";
import type { CondominioRef } from "@/types/condominio";

const STORAGE_KEY = "condominio.proyectoId";
const SIN_CONDOMINIOS: CondominioRef[] = [];

interface CondominioContextValue {
  condominios: CondominioRef[];
  proyectoId: number | null;
  setProyectoId: (id: number) => void;
  isLoading: boolean;
  error: unknown;
  /** true = el usuario efectivo no tiene condominios asignados. */
  sinAcceso: boolean;
}

const CondominioContext = createContext<CondominioContextValue | undefined>(undefined);

/**
 * Provee los condominios visibles y el seleccionado.
 *
 * La lista la decide el servidor (`get_condominios`): solo los proyectos del "Acceso a
 * proyectos" del usuario (o todos si su rol es irrestricto). Al impersonar se pide con
 * `p_auth_user_id` y el servidor la calcula con la identidad del impersonado: se ve exactamente
 * su lista, ni más ni menos. Antes se recortaba aquí, en el navegador, leyendo proyectos_acceso.
 */
export function CondominioProvider({ children }: { children: ReactNode }) {
  const { isImpersonating, impersonatedAuthUserId } = useCondominioImpersonation();
  const { data: condominios = SIN_CONDOMINIOS, isLoading: cargandoLista, error } = useCondominios();

  // Impersonando sin el auth_user_id resuelto todavía: la consulta espera (sin fuga).
  const scopeResolviendo = isImpersonating && !impersonatedAuthUserId;
  const isLoading = cargandoLista;

  const [proyectoId, setProyectoIdState] = useState<number | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? Number(saved) : null;
  });

  // Selección por defecto / re-validación cuando cambia la lista accesible.
  useEffect(() => {
    if (scopeResolviendo || isLoading) return; // sin lista todavía: no se borra la selección
    const valido = proyectoId != null && condominios.some((c) => c.id === proyectoId);
    if (valido) return;
    if (condominios.length > 0) {
      setProyectoIdState(condominios[0].id);
      localStorage.setItem(STORAGE_KEY, String(condominios[0].id));
    } else {
      // Sin condominios accesibles → sin selección (nunca default a 1).
      setProyectoIdState(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [condominios, proyectoId, scopeResolviendo, isLoading]);

  const setProyectoId = (id: number) => {
    setProyectoIdState(id);
    localStorage.setItem(STORAGE_KEY, String(id));
  };

  const value = useMemo<CondominioContextValue>(
    () => ({
      condominios,
      proyectoId,
      setProyectoId,
      isLoading: isLoading || scopeResolviendo,
      error,
      sinAcceso: !isLoading && !scopeResolviendo && condominios.length === 0,
    }),
    [condominios, proyectoId, isLoading, scopeResolviendo, error],
  );

  return <CondominioContext.Provider value={value}>{children}</CondominioContext.Provider>;
}

export function useCondominio() {
  const ctx = useContext(CondominioContext);
  if (!ctx) throw new Error("useCondominio debe usarse dentro de CondominioProvider");
  return ctx;
}
